import {
  Component,
  input,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  signal,
  effect,
  ChangeDetectorRef,
  Injector,
  computed,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { AgentChatView } from "./ai-chat-view";

import { DEFAULT_AGENT_ID, randomUUID } from "@copilotkit/shared";
import { Message, AbstractAgent, AGUIConnectNotImplementedError } from "@ag-ui/client";
import { injectAgentStore } from "../core/agent";
import { AgentKitService } from "../core/agent-kit.service";
import { ChatState } from "../core/chat-state";

@Component({
  selector: "agent-chat",
  standalone: true,
  imports: [CommonModule, AgentChatView],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { "data-agent-ui": "" },
  template: `
    <agent-chat-view
      [messages]="messages() ?? []"
      [autoScroll]="true"
      [messageViewClass]="'w-full'"
      [showCursor]="showCursor()"
    >
    </agent-chat-view>
  `,
  providers: [
    {
      provide: ChatState,
      useExisting: AgentChat,
    },
  ],
})
export class AgentChat implements ChatState {
  readonly inputValue = signal<string>("");

  readonly agentId = input<string | undefined>();

  readonly threadId = input<string | undefined>();

  private readonly resolvedAgentId = computed(() => this.agentId() ?? DEFAULT_AGENT_ID);

  readonly agentStore = injectAgentStore(this.resolvedAgentId);

  private readonly agentKit = inject(AgentKitService);

  readonly cdr = inject(ChangeDetectorRef);

  readonly injector = inject(Injector);

  protected messages = computed(() => this.agentStore().messages());

  protected isRunning = computed(() => this.agentStore().isRunning());

  protected showCursor = signal<boolean>(false);

  private generatedThreadId: string = randomUUID();

  private hasConnectedOnce = false;

  constructor() {
    // Sync agent threadId whenever the store or threadId input changes.
    // Thread lifecycle (clear / seed) is managed by AiChatSessionService + AgentFactory.
    effect(() => {
      const a = this.agentStore().agent;
      if (!a) return;
      a.threadId = this.threadId() || this.generatedThreadId;
      if (!this.hasConnectedOnce) {
        this.hasConnectedOnce = true;
        if ("isCopilotKitAgent" in (a as any)) {
          this.connectToAgent(a);
        }
      }
    });

    effect(() => {
      if (this.isRunning()) {
        this.showCursor.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  private async connectToAgent(agent: AbstractAgent): Promise<void> {
    if (!agent) return;

    this.showCursor.set(true);
    this.cdr.markForCheck();

    try {
      await this.agentKit.core.connectAgent({ agent });
      this.showCursor.set(false);
      this.cdr.markForCheck();
    } catch (error) {
      if (error instanceof AGUIConnectNotImplementedError) {
        // Connect not implemented, ignore
      } else {
        console.error("Failed to connect to agent:", error);
      }
      this.showCursor.set(false);
      this.cdr.markForCheck();
    }
  }

  async submitInput(value: string): Promise<void> {
    const agent = this.agentStore().agent;
    if (!agent || !value.trim()) return;

    const userMessage: Message = {
      id: randomUUID(),
      role: "user",
      content: value,
    };
    agent.addMessage(userMessage);

    this.inputValue.set("");
    this.showCursor.set(true);
    this.cdr.markForCheck();

    try {
      await this.agentKit.core.runAgent({ agent });
    } catch (error) {
      console.error("[AgentChat] runAgent ERROR:", error);
    } finally {
      this.showCursor.set(false);
      this.cdr.markForCheck();
    }
  }

  changeInput(value: string): void {
    this.inputValue.set(value);
  }
}
